import cn from 'classnames';

import { getClampedMarkOffset, isMarkActive, toPercent } from '../utils';

import type { ReactElement } from 'react';

import type { Axis, AxisProps, Mark, SliderProps } from '../types';

interface SliderMarkListProps {
  axis: Axis;
  axisProps: AxisProps;
  markLevels: number[];
  markRenderKeys: string[];
  marks: Mark[];
  max: number;
  min: number;
  range: boolean;
  track: SliderProps['track'];
  trackLeap: number;
  trackOffset: number;
}

/*
 * Renders the list of mark indicators and their optional labels along the slider track.
 *
 * Each mark renders:
 * - A small dot (`rbui-slider__mark`) positioned at the mark's percentage.
 * - An optional label (`rbui-slider__mark-label`) with stagger support. Labels that
 *   would overlap are pushed to lower rows (levels 1, 2) and connected to the track
 *   via a vertical stem line.
 */
export const SliderMarkList = ({
  axis,
  axisProps,
  markLevels,
  markRenderKeys,
  marks,
  max,
  min,
  range,
  track,
  trackLeap,
  trackOffset,
}: SliderMarkListProps): ReactElement => {
  return (
    <>
      {marks.map((mark, index) => {
        const percent = toPercent(mark.value, min, max);
        const isActive = isMarkActive(percent, range, track, trackOffset, trackLeap);

        const dotStyle = getClampedMarkOffset(percent, axis);
        const labelStyle = axisProps[axis].offset(percent);
        const level = mark.label != null ? (markLevels[index] ?? 0) : 0;

        return (
          <span key={markRenderKeys[index]}>
            <span
              className={cn('rbui-slider__mark', {
                'rbui-slider__mark--active': isActive,
              })}
              data-index={index}
              data-mark-index={index}
              style={dotStyle}
            />
            {mark.label != null && (
              <span
                aria-hidden
                className={cn('rbui-slider__mark-label', {
                  'rbui-slider__mark-label--active': isActive,
                })}
                data-index={index}
                data-level={level}
                data-mark-index={index}
                style={labelStyle}
              >
                {level > 0 && <span className="rbui-slider__mark-stem" style={{ height: `${4 + level * 20}px` }} />}
                <span className="rbui-slider__mark-label-text">{mark.label}</span>
              </span>
            )}
          </span>
        );
      })}
    </>
  );
};

SliderMarkList.displayName = 'SliderMarkList';
